import React, { useState } from 'react';
import {
    Box,
    Button,
    useToast,
    Text,
    VStack,
    Icon,
} from '@chakra-ui/react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import GrillaEffect from "views/Grillas/GrillaEffect";
import dayjs from "dayjs";
import 'dayjs/locale/es';
import { IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useHistory } from 'react-router-dom';

registerLocale("es", es);
const theme = createTheme({
    palette: {}
});

const RegistrosConciliados = () => {
    const defaultFecha = [
        { id: 'fecha_movimiento', value: [dayjs().subtract(100, 'days'), dayjs(new Date())] }
    ];
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // CAMBIO: Inicializar con defaultFecha
    const [filters, setFilters] = useState([...defaultFecha]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const history = useHistory();
    const toast = useToast();

    const handleRevertir = async (id) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tesoreriacomprobantes/items/registros/revertir-conciliacion/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": localStorage.getItem("token"),
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                if (errorData.error === 'El registro no está conciliado.') {
                    toast({
                        title: 'Información',
                        description: 'El registro ya ha sido revertido.',
                        status: 'info',
                        duration: 3000,
                        isClosable: true,
                    });
                } else {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            } else {
                toast({
                    title: 'Conciliación revertida.',
                    description: `El estado de conciliación del registro con ID ${id} fue modificado.`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                // CAMBIO: Usar refreshTrigger para forzar recarga de datos
                setRefreshTrigger(prev => prev + 1);
            }
        } catch (error) {
            console.error('Error revirtiendo la conciliación:', error);
            toast({
                title: 'Error',
                description: 'No se pudo cambiar el estado de conciliación del registro.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const confirmRevertir = (id) => {
        toast({
            title: 'Confirmar Acción',
            description: `¿Desea revertir la conciliación del registro con ID ${id}?`,
            status: 'info',
            duration: null,
            isClosable: true,
            position: 'top',
            render: () => (
                <Box
                    bg="white"
                    p={6}
                    borderRadius="lg"
                    boxShadow="lg"
                    textAlign="center"
                >
                    <p style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "16px" }}>
                        ¿Desea revertir la conciliación del registro con ID {id}?
                    </p>
                    <Button
                        bg="teal.500"
                        color="white"
                        borderRadius="md"
                        px={6}
                        py={2}
                        fontSize="16px"
                        _hover={{ bg: "teal.600" }}
                        onClick={async () => {
                            await handleRevertir(id);
                            toast.closeAll();
                        }}
                        mr={3}
                    >
                        Confirmar
                    </Button>
                    <Button
                        bg="gray.300"
                        color="black"
                        borderRadius="md"
                        px={6}
                        py={2}
                        fontSize="16px"
                        _hover={{ bg: "gray.400" }}
                        onClick={() => toast.closeAll()}
                    >
                        Cancelar
                    </Button>
                </Box>
            ),
        });
    };

    const handleRevertirMultiple = async () => {
        if (selectedRows.length === 0) return;
        setIsLoading(true);
        try {
            for (const row of selectedRows) {
                await handleRevertir(row.id_registro);
            }
            setSelectedRows([]);
            // CAMBIO: Usar refreshTrigger en lugar de handleSearch
            setRefreshTrigger(prev => prev + 1);
        } catch (error) {
            // El toast de error ya está en handleRevertir
        } finally {
            setIsLoading(false);
        }
    };

    // CAMBIO: Función para limpiar todos los filtros
    const handleClearFilters = () => {
        setFilters([...defaultFecha]); // Mantener solo el filtro de fecha por defecto
        setRefreshTrigger(prev => prev + 1);
        toast({
            title: 'Filtros limpiados',
            description: 'Se han eliminado todos los filtros aplicados excepto el rango de fechas.',
            status: 'info',
            duration: 2000,
            isClosable: true,
        });
    };

    // CAMBIO: Componente para mostrar cuando no hay resultados
    const NoResultsMessage = () => (
        <VStack spacing={4} p={8} textAlign="center">
            <Icon as={SearchOffIcon} boxSize={12} color="gray.400" />
            <Text fontSize="lg" color="gray.600">
                No se encontraron registros conciliados con los filtros aplicados
            </Text>
            <Text fontSize="sm" color="gray.500">
                Intenta ajustar los criterios de búsqueda o limpiar los filtros
            </Text>
            <Button
                leftIcon={<ClearAllIcon />}
                colorScheme="blue"
                variant="outline"
                onClick={handleClearFilters}
            >
                Limpiar filtros adicionales
            </Button>
        </VStack>
    );

    // CAMBIO: Custom toolbar con botón adicional de limpiar filtros
    const customTopToolbar = (
        <Box display="flex" alignItems="center" gap={2}>
            <IconButton
                color="primary"
                disabled={selectedRows.length === 0}
                onClick={handleRevertirMultiple}
                title="Revertir Seleccionados"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
                <DoneAllIcon sx={{ mr: 1 }} />
                Revertir Seleccionados
            </IconButton>
            <IconButton
                color="secondary"
                disabled={filters.length <= 1} // Deshabilitado si solo tiene el filtro de fecha por defecto
                onClick={handleClearFilters}
                title="Limpiar Filtros"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
                <ClearAllIcon sx={{ mr: 1 }} />
                Limpiar Filtros
            </IconButton>
            <Button
                variant="contained"
                color="secondary"
                onClick={() => history.push('/admin/conciliarops')}
                leftIcon={<ArrowBackIcon />}
            >
                Volver a Conciliar OPS
            </Button>
            <span>
                {selectedRows.length > 0 ? `${selectedRows.length} seleccionados` : ''}
                {filters.length > 1 && ` | ${filters.length - 1} filtros adicionales`}
            </span>
        </Box>
    );

    const onRowSelectionChange = (rowSelection, table) => {
        const selected = Object.keys(rowSelection)
            .filter(rowId => rowSelection[rowId])
            .map(rowId => table.getRowModel().rows.find(r => r.id === rowId)?.original)
            .filter(Boolean);
        setSelectedRows(selected);
    };

    const columnNames = {
        fecha_movimiento: "Fecha Pago",
        id_comprobante_extbc: "ID EXTBC",
        id_comprobante_lops: "ID LOPS",
        concepto_descripcion: "CTA BCO",
        razonSocial: "Razon Social",  // Asegurarse que coincida exactamente con la clave del backend
        num_comprobante: "N° Cheque/N° Transferencia",
        importe: "Importe",           // Asegurarse que coincida exactamente con la clave del backend
        id_registro: "ID Reg. LOPS",
        acciones: "Acciones",
    };

    const colsGrillaHidden = {
        id_registro: false,
    };

    return (
        <Box p={4} mt="22%">
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                <ThemeProvider theme={theme}>
                    {/* CAMBIO: Mostrar mensaje cuando no hay resultados */}
                    {data.length === 0 && !isLoading && filters.length > 1 ? (
                        <NoResultsMessage />
                    ) : (
                        <GrillaEffect
                            key={refreshTrigger}
                            data={data}
                            updateData={setData}
                            filters={filters}
                            updateFilters={setFilters}
                            colsHeaders={columnNames}
                            hiddenColumns={colsGrillaHidden}
                            method='GET'
                            baseURI={`${process.env.REACT_APP_API_URL}/tesoreriacomprobantes/items/registros-conciliados`}
                            handleRevertir={confirmRevertir}
                            customTopToolbar={customTopToolbar}
                            onRowSelectionChange={onRowSelectionChange}
                            isLoading={isLoading}
                            displayColumnDefOptions={{
                                'mrt-row-actions': {
                                    size: 100,
                                    muiTableHeadCellProps: {
                                        align: 'center',
                                    },
                                },
                            }}
                            rowSelectionEnabled={true}
                        />
                    )}
                </ThemeProvider>
            </LocalizationProvider>
        </Box>
    );
};

export default RegistrosConciliados;